import { ChevronRight, Ellipsis } from 'lucide-react';
import React, { useState } from 'react';

import {
  cn,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  LabeledIcon,
} from '@crossnokaye/ux-common';

import { AtlasRouteIds } from '@/components/navigation/constants';
import { NavTooltipDropdownTrigger } from '@/components/navigation/left/components/shared/nav-tooltip-dropdown-trigger';
import { SequencerNav } from '@/components/navigation/left/components/shared/sequencer-nav';
import { useNavConfig } from '@/components/navigation/left/components/shared/use-nav-config';
import { NavLink } from '@/components/navigation/nav-link';
import { NavItemConfig } from '@/components/navigation/types';

export const MoreMenu = ({
  items,
  handleSelectOption,
  selectedId,
}: {
  items: Record<string, NavItemConfig[]>;
  handleSelectOption: (id: string) => void;
  selectedId: string | undefined;
}) => {
  const { getNavItemConfig } = useNavConfig();
  const [isOpen, setIsOpen] = useState(false);

  const categories = Object.entries(items);

  const moreSubItemSelected = Object.values(items)
    .flat()
    .some((item) => item.id === selectedId);

  return (
    <NavTooltipDropdownTrigger
      dropdownTriggerProps={{ onClick: (e) => e.stopPropagation() }}
      label={'Click to View More Options'}
      menuOpen={isOpen}
      onMenuOpenChange={setIsOpen}
      trigger={
        <LabeledIcon
          icon={<Ellipsis className="glyph glyph-sm" aria-hidden="true" />}
          label={'More'}
          className={cn('cursor-pointer', {
            'bg-popover': isOpen,
            'bg-primary/20 text-primary hover:bg-primary/30': moreSubItemSelected,
            'bg-primary/30 hover:bg-primary/30': isOpen && moreSubItemSelected,
          })}
          data-testid={'collapsed-nav-more-menu'}
        />
      }
    >
      <DropdownMenuContent
        className={'border-input border min-w-48 cursor-pointer'}
        side={'right'}
        align={'start'}
        data-testid={'collapsed-nav-more-menu-content'}
        withPortal
      >
        {categories.map(([category, categoryItems], index) => (
          <React.Fragment key={category}>
            <DropdownMenuLabel
              className={'text-muted-foreground uppercase flex justify-start text-xs'}
            >
              {category}
            </DropdownMenuLabel>
            <DropdownMenuGroup>
              {categoryItems.map((item) => {
                const { sequencers, useSequencerNav, equipmentNavSearch } = getNavItemConfig(item);

                if (useSequencerNav && sequencers) {
                  return (
                    // Prevent triggerExpand from being called
                    <div onClick={(e) => e.stopPropagation()}>
                      <SequencerNav
                        key={item.id}
                        routeId={item.id}
                        sequencerDevices={sequencers}
                        onSelect={() => {
                          setIsOpen(false);
                          handleSelectOption(item.id);
                        }}
                      >
                        <div
                          key={item.id}
                          data-testid={`collapsed-nav-more-menu-item-${item.id}`}
                          // Styles to match the DropdownMenuItem
                          className={cn(
                            'relative flex select-none items-center justify-between rounded-sm px-2 py-1.5 text-sm outline-none transition-colors focus:bg-accent',
                            'focus:text-accent-foreground data-disabled:pointer-events-none data-disabled:opacity-50 cursor-pointer',
                            'hover:bg-accent'
                          )}
                        >
                          <span>{item.label}</span>
                          <ChevronRight className="glyph glyph-sm" aria-hidden="true" />
                        </div>
                      </SequencerNav>
                    </div>
                  );
                }

                return (
                  <NavLink
                    key={item.id}
                    routeId={item.id}
                    searchParams={equipmentNavSearch}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <DropdownMenuItem
                      key={item.id}
                      data-testid={`collapsed-nav-more-menu-item-${item.id}`}
                      className={cn(
                        'cursor-pointer',
                        item.id === selectedId && 'bg-primary/20 text-primary hover:bg-primary/30',
                        item.id === AtlasRouteIds.Support && 'text-muted-foreground'
                      )}
                      onClick={() => handleSelectOption(item.id)}
                    >
                      {item.label}
                    </DropdownMenuItem>
                  </NavLink>
                );
              })}
            </DropdownMenuGroup>
            {index < categories.length - 1 && <DropdownMenuSeparator />}
          </React.Fragment>
        ))}
      </DropdownMenuContent>
    </NavTooltipDropdownTrigger>
  );
};
